import React from "react";
import {IconFavoriteDone, IconCheckMark} from "components/icons";
import {color} from "constants/color.consts";
import {CustomTooltip} from "components/CustomTooltip";
import {ConfirmModal} from "components/ConfirmModal";
import i18next from "i18next";
import * as qr from "querystring-es3";
// locale
import {SAVE_SEARCH_REGIONS} from "constants/routes.const";
import {
  selectFieldName,
  defaultDirectionsByField,
  subscriptionPriceKeys,
} from "constants/user.consts";
import {getStorage, removeStorage} from "./storage";
import AuthStore from "../store/auth.store";
import {showSuccess, showInfo} from "./notifications.helper";

export const notNull = (value) => value !== null;
export const isNull = (value) => value === null;

export const notUndefined = (value) => value !== undefined;
export const isUndefined = (value) => value === undefined;

export const notEmpty = (value) => value !== "";

export const hasValue = (value) =>
  notNull(value) && notUndefined(value) && notEmpty(value);

export const checkField = (msg) => {
  const str = msg.replace(/([A-Z])/g, " $1").toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function updateLocationHash(hash) {
  window.location.hash = hash;
  const cleanUrl = window.location.href.replace(/#$/, "");
  window.history.replaceState(null, "", cleanUrl);
}

const checkJoinRequest = (auth) => {
  const {joinRequest} = auth;
  if (auth && joinRequest && joinRequest.length) {
    const {
      Company: {companyNumber},
      OrganizationJoinRequest: {OrganizationId},
    } = joinRequest[0];
    return {companyNumber, organizationId: OrganizationId};
  } else {
    return {companyNumber: getStorage("companyNumber"), organizationId: null};
  }
};

export const createNewOrganization = (organization, auth, navigate, companyName) => {
  return () => {
    organization.createNewOrg(checkJoinRequest(auth), companyName).then(() => {
      auth.checkToken().then(() => {
        removeStorage("companyNumber");
        navigate(SAVE_SEARCH_REGIONS);
      });
      auth.updateAuthModalName();
    });
  };
};

export const digitsBy2 = (x) => {
  return Number.parseFloat(x).toFixed(2);
};

export const createArray = (lng) => Array.from(new Array(lng));

export const createLanguageKeysArray = (length, baseKey, withoutZero) =>
  createArray(length).map(
    (_, number) =>
      `${baseKey}_${
        !withoutZero && `${number}`.length < 2 ? `0${number + 1}` : number + 1
      }`
  );

export const constTemplate = ({name, number, withoutZero}) => {
  name = name.toString().toUpperCase();
  number += 1;
  number = number.toString();
  number = !withoutZero && number.length < 2 ? `0${number}` : number;
  return {
    label: `${name}_${number}`,
    value: `${name}_${number}`,
  };
};

export const openNewTab = (url) => {
  url = url.match(/^http[s]?:\/\//) ? url : `http://${url}`;
  window.open(url, "_blank", "noreferrer");
};

export const toExternalLink = (url) => {
  url = url.match(/^http[s]?:\/\//) ? url : `http://${url}`;
  window.location = url;
};

export const trimString = (str, length = 20) => {
  if (str) return str.length <= length ? str : `${str.substring(0, length)}...`;
};

export const getDomain = (url, subdomain) => {
  if (!url) return null;

  subdomain = subdomain || false;

  url = url.replace(/(https?:\/\/)?(www.)?/i, "");

  if (!subdomain) {
    url = url.split(".");

    url = url.slice(url.length - 2).join(".");
  }

  if (url.indexOf("/") !== -1) return url.split("/")[0];

  return url;
};

export const sumKeyValueObjectsInArray = (items, key) => {
  if (Array.isArray(items)) {
    return items.reduce((a, b) => a + (b[key] || 0), 0);
  }
};

export const getInitials = (firstName, lastName) => {
  return `${firstName ? firstName.trim()[0] : ""}${
    lastName ? lastName.trim()[0] : ""
  }`.toUpperCase();
};

export const getFullName = (firstName, lastName) =>
  `${firstName ?? ""} ${lastName ?? ""}`.trim();

// export const formatCompanyNumber = (value, restore) => {
//   /***
//    * return company format as xxxx-xxxxxx
//    * else if restore === true, return restored value (xxxx-xxxxxx -> xxxxxxxxxx)
//    * ***/
//   if (!value) return "";

//   if (restore === true) return value.toString().trim().replace(/[-/x]/g, "");

//   if (value.length > 5) return `${value.substring(0, 6)}-${value.substring(6)}`;
//   return value;
// };

export const isValidOrganisationNumber = (digits) => {
  if (!/^\d{10}$/.test(digits)) return false;

  const arr = digits.split("").map((d) => parseInt(d, 10));
  const checkDigit = arr[9];
  const body = arr.slice(0, 9);

  let sum = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    const posFromRight = body.length - 1 - i;
    const weight = posFromRight % 2 === 0 ? 2 : 1;
    let prod = body[i] * weight;
    if (prod > 9) prod -= 9;
    sum += prod;
  }

  const expectedCheck = (10 - (sum % 10)) % 10;
  return expectedCheck === checkDigit;
};

export const formatCompanyNumber = (value, restore) => {
  if (value == null) return "";

  const str = String(value).trim();
  const digitsOnly = str.replace(/\D/g, "");

  if (restore) {
    // направление: убрать дефис, если валиден
    return isValidOrganisationNumber(digitsOnly) && /^\d{10}$/.test(digitsOnly)
      ? digitsOnly
      : str;
  }

  // направление: добавить дефис, если валиден
  if (isValidOrganisationNumber(digitsOnly) && /^\d{10}$/.test(digitsOnly)) {
    return `${digitsOnly.substring(0, 6)}-${digitsOnly.substring(6)}`;
  }

  return str;
};

export const getBankIDStatus = (message) => {
  const language = i18next.language || "en";

  if (typeof message === "string") return message;

  if (typeof message === "object" && message !== null)
    return message[language] || message["en"] || "";

  return "";
};

export const newQuerySearch = (searchString) => {
  const query = qr.parse(searchString.replace("?", ""));
  const newQuery = {};
  Object.keys(query).forEach((item) => {
    const queryParam = query[item];
    const queryParamArray = queryParam.split(",");
    if (item.includes("[]")) {
      newQuery[item.replace("[]", "")] = queryParamArray;
    } else newQuery[item] = query[item];
  });
  return newQuery;
};

export const changeSelectValue = (list, filterParams) =>
  list[0].value.includes(filterParams?.field?.toUpperCase())
    ? list.find((item) => item.value.includes(filterParams.direction)).value
    : list[0].value;

export const changeFilterParams = (value) => {
  const field = selectFieldName[value.split("_")[0]] ?? "";
  const direction = value.split("_")[1] ?? "";
  const params = {field, direction};
  if (!value) {
    delete params["direction"];
  }
  return params;
};

export const directionReplacement = (filterParams) =>
  filterParams?.direction && filterParams.direction === "default"
    ? {...filterParams, direction: defaultDirectionsByField[filterParams.field]}
    : filterParams;

export const copyToClipboard = (text, noMessage, message) => {
  const textField = document.createElement("textarea");
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand("copy");
  textField.remove();
  !noMessage && showInfo(message ?? "Text copied", 2);
};

export const parentElementIdWidth = (id) =>
  document.getElementById(id)?.parentElement?.clientWidth ?? 0;

export const parentElementIdHeight = (id) =>
  document.getElementById(id)?.parentElement?.clientHeight ?? 0;

export const hasPermission = (canPermissions) =>
  canPermissions?.some((permission) =>
    AuthStore?.user?.permissions?.map((item) => item.PermissionId).includes(permission)
  ) ?? false;

export const hasPermissions = (canPermissions) =>
  canPermissions?.every((permission) =>
    AuthStore?.user?.permissions?.map((item) => item.PermissionId).includes(permission)
  ) ?? false;

export const hasUserSubscription = (canSubscriptions) =>
  canSubscriptions?.some((subscription) =>
    AuthStore?.user?.SubscribeUserUnit?.SubscribeProducts?.map(
      (item) => item.name
    ).includes(subscription)
  ) ?? false;

export const hasUserSubscriptions = (canSubscriptions) =>
  canSubscriptions?.every((subscription) =>
    AuthStore?.user?.SubscribeUserUnit?.SubscribeProducts?.map(
      (item) => item.name
    ).includes(subscription)
  ) ?? false;

export const hasPermissionQuantity = (canPermission) =>
  hasValue(
    !!AuthStore?.user?.permissions?.find(
      (permission) => permission.PermissionId === canPermission
    )?.quantity
  );

export const hasCountPermission = (canPermission, countPermission) =>
  AuthStore?.user?.permissions?.find(
    (permission) => permission.PermissionId === canPermission
  )?.quantity > countPermission || false;

export const hasUserPermission = (actionsRoles, clientRole) => {
  return actionsRoles.includes(clientRole);
};

export const saveDocument = (data, type, fileName, message) => {
  const blob = new Blob([data], {type});
  const csvURL = window.URL.createObjectURL(blob);
  let tempLink = document.createElement("a");
  tempLink.href = csvURL;
  tempLink.setAttribute("download", fileName);
  tempLink.click();
  !!message && showSuccess(message);
  window.URL.revokeObjectURL(blob);
  tempLink.remove();
};

export const isObject = (obj) => obj === Object(obj);

export const arrayToCommaAnd = (items, lastAnd = " and ", comma = ", ") =>
  items.reduce(
    (str, item, i) =>
      (str += `${item}${
        i < items.length - 2 ? comma : i < items.length - 1 ? lastAnd : ""
      }`),
    [""]
  );

export const hasTranslation = (key) => i18next.t(key) !== key;

export const chooseFavoriteIcon = (isGreenIcon, isEmptyIcon) =>
  isGreenIcon ? (
    isEmptyIcon ? (
      <IconCheckMark color={color.green} addClass="mr-2" />
    ) : (
      <IconFavoriteDone color={color.green} addClass="mr-2" />
    )
  ) : (
    <CustomTooltip text={"SUPPLIER_QUALIFICATION_NOT_PASSED_QUALIFICATION"}>
      <div style={{height: "20px", display: "inline", marginRight: "10px"}}>
        {isEmptyIcon ? (
          <IconCheckMark color={color.gold} />
        ) : (
          <IconFavoriteDone color={color.gold} />
        )}
      </div>
    </CustomTooltip>
  );

export const hasNoLegacySupplierQualification = (
  isTitleIcon,
  isFavorite,
  hasSuppliersQualifications,
  isEnableSuppliersQualification,
  isEmptyIcon
) =>
  (isTitleIcon && isFavorite) ||
  (!isTitleIcon && !hasSuppliersQualifications && isEnableSuppliersQualification) ? (
    chooseFavoriteIcon(
      hasSuppliersQualifications || !isEnableSuppliersQualification,
      isEmptyIcon
    )
  ) : (
    <React.Fragment></React.Fragment>
  );

// Augment String.prototype to allow for easier formatting.  This implementation
// doesn't completely destroy any existing String.prototype.format functions,
// and will stringify objects/arrays. (arguments into string - {0}, {1}, etc.)
// eslint-disable-next-line no-extend-native
String.prototype.format = (function (i, safe, arg) {
  function format() {
    let str = this;
    let len = arguments.length + 1;
    for (i = 0; i < len; arg = arguments[i++]) {
      safe = typeof arg === "object" ? JSON.stringify(arg) : arg;
      str = str.replace(RegExp("\\{" + (i - 1) + "\\}", "g"), safe);
    }
    return str;
  }
  format.native = String.prototype.format;
  return format;
})();

export const subscriptionPrice = (subscription, period) =>
  subscription[subscriptionPriceKeys[period]]?.replace(".00", "");

export const parseUrlParams = (params) =>
  Object.entries(params).reduce(
    (obj, item) => ({
      ...obj,
      [item[0].replace("[]", "")]: item[0].includes("[]") ? item[1].split(",") : item[1],
    }),
    {}
  );

export const getUrlParams = (params, additionalParams, objectParamsOnly) => {
  const urlParams = Object.entries(params || {}).reduce(
    (obj, item) => ({
      ...obj,
      [`${item[0]}${Array.isArray(item[1]) ? "[]" : ""}`]: item[1],
    }),
    {}
  );
  const isEmpty = !Object.keys(urlParams).length;
  return isEmpty
    ? ""
    : objectParamsOnly
    ? {...urlParams, ...(additionalParams ?? {})}
    : `?${new URLSearchParams({...urlParams, ...(additionalParams ?? {})})}`;
};
export const getUserDropdownList = (users) =>
  users
    ?.map((user) => ({
      value: user.id,
      label: getFullName(user.firstName, user.lastName),
    }))
    ?.filter((user) => user.label) ?? [];

export const obfuscatePersonalNumber = (pnr) =>
  pnr ? `${pnr.slice(0, -4)}****` : "************";

/* eslint-disable no-extend-native */
Array.prototype.collatorSortString = function () {
  const language = i18next.language;
  return this.slice().sort((a, b) =>
    new Intl.Collator(language, {
      sensitivity: "variant",
      caseFirst: "upper",
    }).compare(a, b)
  );
};

/* eslint-disable no-extend-native */
Array.prototype.collatorSortObj = function (key) {
  const language = i18next.language;
  const [mainKey, subKey] = key.split(".");
  const collator = new Intl.Collator(language, {
    sensitivity: "variant",
    caseFirst: "upper",
  });
  return this.slice().sort((a, b) =>
    collator.compare(
      subKey ? a[mainKey][subKey] : a[mainKey],
      subKey ? b[mainKey][subKey] : b[mainKey]
    )
  );
};

/* eslint-disable no-extend-native */
Array.prototype.noop = function () {
  return this;
};

export const isEmptyObject = (obj) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) return false;
  }
  return true;
};

export const filterObjectValidKeys = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([_, value]) => value != null));

export const formFileData = (values) => {
  const data = new FormData();
  data.append("file", values);
  return data;
};

export const uploadFiles = (files, s3Upload, updateEntity, setLoading) => {
  !!setLoading && setLoading(true);
  const s3UploadFiles = files.map((file) => s3Upload(file));
  Promise.all(s3UploadFiles)
    .then(
      (results) =>
        !!updateEntity &&
        updateEntity(
          results.map((file) => ({id: window.crypto.randomUUID(), File: file}))
        )
    )
    .finally(() => !!setLoading && setLoading(false));
};

export const removeFilesConfirmModal = (id, remove) =>
  ConfirmModal({
    title: i18next.t(id ? "WANT_DELETE_FILE" : "WANT_DELETE_ALL_FILES"),
    text: i18next.t(id ? "CANT_DELETE_FILE" : "CANT_DELETE_ALL_FILES"),
    type: "warning",
    onOk: () => remove(id),
    noCancelBefore: true,
  });

export const enableEntity = (entity, rule) => (rule ? entity : null);
export const disableEntity = (entity, rule) => (rule ? null : entity);

export const printPage = (pageTitle) => {
  const originalTitle = document.title;
  document.title = pageTitle;
  window.print();
  document.title = originalTitle;
};
export const getSelectListMembers = (members, key = "UserId") =>
  members?.length
    ? members
        ?.map((member) => ({
          value: member[key],
          label: getFullName(member.User?.firstName, member.User?.lastName),
        }))
        .filter((val) => val.label)
    : [];

// preparing data of specific offer for updateOurOffer endpoint
export const generateOfferData = (offer, request) => ({
  ...offer,
  ProposalId: request.id ?? null,
  RequestId: request.Request?.id,
});

export const scrollToTop = () => window.scrollTo({top: 0, behavior: "smooth"});
export const scrollToBottom = () =>
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: "smooth",
  });

export const scrollToTopClassname = (classname) =>
  document.getElementsByClassName(classname)[0].scrollIntoView({behavior: "smooth"});

export const normalizeParams = ({params, allowedKeys, listTypeKeys, allowedValues}) => {
  const ALLOWED_VALUES = {
    field: ["createdAt", "closedAt", "name", "publishedAt"],
    direction: ["ASC", "DESC", "default"],
    order: ["ASC", "DESC"],
    sort: ["name", "createdAt", "closedAt", "publishedAt"],
    page: createArray(100).map((_, i) => (++i).toString()),
  };

  const normalized = {};

  for (const [key, rawValue] of Object.entries(params)) {
    if (allowedKeys && !allowedKeys.includes(key)) continue;
    const values = allowedValues?.[key]?.length
      ? allowedValues[key]
      : ALLOWED_VALUES[key] || [];
    // if the key values are needed in an array
    const needStringToArray =
      listTypeKeys && listTypeKeys.includes(key) && !Array.isArray(rawValue);

    if (!values?.length && !needStringToArray) {
      normalized[key] = rawValue;
      continue;
    }

    if (Array.isArray(rawValue) && !rawValue.filter((val) => values.includes(val)).length)
      continue;

    if (
      needStringToArray &&
      !rawValue?.split(",").filter((val) => values.includes(val)).length
    )
      continue;
    normalized[key] = Array.isArray(rawValue)
      ? rawValue.filter((val) => values.includes(val))
      : needStringToArray
      ? rawValue.split(",").filter((val) => values.includes(val))
      : values.find((val) => val === rawValue) ??
        allowedValues?.[key]?.[0] ??
        ALLOWED_VALUES[key][0];
  }
  return normalized;
};

export const trimObjectValues = (obj) => {
  const normalized = {...obj};
  for (const key in obj)
    normalized[key] =
      typeof normalized[key] === "string" ? normalized[key]?.trim() : normalized[key];
  return normalized;
};

export const formatCityAscii = (value) => {
  if (!value) return value;
  return value.toLowerCase().replace(/\p{L}+/gu, (word) => {
    const [firstChar, ...restChars] = Array.from(word);
    return `${firstChar.toUpperCase()}${restChars.join("")}`;
  });
};
